import { api } from "./axiosInstance";

export type OpportunitySubcategory = {
  id: string;
  name: string;
  category_id: string;
  sort_order: number;
};

export type OpportunityCategoryColumn = {
  id: string;
  field_key: string;
  header: string;
  field_type: string;
  is_primary_sort_field: boolean;
  sort_order: number;
  open_edit: boolean;
};

export type OpportunityCategory = {
  id: string;
  name: string;
  sort_order: number;
  subcategories: OpportunitySubcategory[];
  columns: OpportunityCategoryColumn[];
};

export type Opportunity = {
  id: string;
  category_id: string;
  primary_date: string | null;
  end_date: string | null;
  future_stops: number;
  subcategory_id: string | null;
  subcategory_name: string | null;
  fields: Record<string, string | null>;
  is_derived: boolean;
};

export type CreateOpportunityPayload = {
  category_id: string;
  primary_date?: string | null;
  subcategory_id?: string | null;
  fields: Record<string, string | null>;
};

export type UpdateOpportunityPayload = {
  category_id?: string;
  primary_date?: string | null;
  subcategory_id?: string | null;
  fields?: Record<string, string | null>;
};

export const apiGetOpportunityCategories = async (): Promise<OpportunityCategory[]> => {
  const response = await api.get("/api/v1/opportunity-categories");
  return response.data;
};

export type SubcategoryCounts = Record<string, number>;

export const apiGetOpportunities = async (categoryId: string, subcategoryId?: string | null): Promise<Opportunity[]> => {
  const params: Record<string, string> = { categoryId };
  if (subcategoryId) params.subcategoryId = subcategoryId;
  const response = await api.get("/api/v1/opportunities", { params });
  return response.data;
};

export const apiGetSubcategoryCounts = async (categoryId: string): Promise<SubcategoryCounts> => {
  const response = await api.get("/api/v1/opportunities/subcategory-counts", { params: { categoryId } });
  return response.data;
};

export const apiCreateOpportunity = async (payload: CreateOpportunityPayload): Promise<Opportunity> => {
  const response = await api.post("/api/v1/opportunities", payload);
  return response.data;
};

export const apiUpdateOpportunity = async (id: string, payload: UpdateOpportunityPayload): Promise<void> => {
  await api.patch(`/api/v1/opportunities/${id}`, payload);
};

export const apiDeleteOpportunity = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/opportunities/${id}`);
};

type ExportJobResponse = { jobId: string };
type ExportStatusResponse = {
  id: string;
  state: "Queued" | "Processing" | "Completed" | "Failed";
  downloadUrl?: string;
  error?: string;
};

export type OpportunityExportParams = {
  categoryId: string;
  subcategoryId?: string | null;
};

const apiStartOpportunityExport = async (
  params: OpportunityExportParams
): Promise<ExportJobResponse> => {
  const queryParams: Record<string, string> = { categoryId: params.categoryId };
  if (params.subcategoryId) queryParams.subcategoryId = params.subcategoryId;
  const response = await api.post<ExportJobResponse>("/api/v1/opportunities/export", null, { params: queryParams });
  return response.data;
};

const apiGetOpportunityExportStatus = async (
  jobId: string
): Promise<ExportStatusResponse> => {
  const response = await api.get<ExportStatusResponse>(`/api/v1/opportunities/export/${jobId}`);
  return response.data;
};

const EXPORT_POLL_INTERVAL = 500;
const EXPORT_TIMEOUT = 30_000;

export const apiExportOpportunities = async (
  params: OpportunityExportParams
): Promise<void> => {
  const { jobId } = await apiStartOpportunityExport(params);

  const startTime = Date.now();
  while (Date.now() - startTime < EXPORT_TIMEOUT) {
    await new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_INTERVAL));
    const status = await apiGetOpportunityExportStatus(jobId);

    if (status.state === "Completed" && status.downloadUrl) {
      window.open(status.downloadUrl, "_blank");
      return;
    }

    if (status.state === "Failed") {
      throw new Error(status.error ?? "Export failed");
    }
  }

  throw new Error("Export timed out");
};

// ── Admin: Category management ──

export type CategoryUsage = {
  opportunity_count: number;
  subcategories: { id: string; name: string; count: number }[];
};

export const apiCreateOpportunityCategory = async (name: string, sortOrder: number): Promise<OpportunityCategory> => {
  const response = await api.post("/api/v1/opportunity-categories", { name, sort_order: sortOrder });
  return response.data;
};

export const apiUpdateOpportunityCategory = async (id: string, name: string, sortOrder: number): Promise<void> => {
  await api.patch(`/api/v1/opportunity-categories/${id}`, { name, sort_order: sortOrder });
};

export const apiDeleteOpportunityCategory = async (id: string, reassignTo?: string): Promise<void> => {
  const params: Record<string, string> = {};
  if (reassignTo) params.reassignTo = reassignTo;
  await api.delete(`/api/v1/opportunity-categories/${id}`, { params });
};

export const apiGetCategoryUsage = async (id: string): Promise<CategoryUsage> => {
  const response = await api.get(`/api/v1/opportunity-categories/${id}/usage`);
  return response.data;
};

// ── Admin: Column management ──

export type ColumnDefinition = {
  field_key: string;
  header: string;
  field_type: string;
  is_primary_sort_field: boolean;
  sort_order: number;
  open_edit: boolean;
};

export const apiGetCategoryColumns = async (categoryId: string): Promise<OpportunityCategoryColumn[]> => {
  const response = await api.get(`/api/v1/opportunity-categories/${categoryId}/columns`);
  return response.data;
};

export const apiUpdateCategoryColumns = async (categoryId: string, columns: ColumnDefinition[]): Promise<void> => {
  await api.put(`/api/v1/opportunity-categories/${categoryId}/columns`, { columns });
};

// ── Admin: Subcategory management ──

export const apiGetSubcategories = async (categoryId: string): Promise<OpportunitySubcategory[]> => {
  const response = await api.get("/api/v1/opportunity-subcategories", { params: { categoryId } });
  return response.data;
};

export const apiCreateSubcategory = async (name: string, categoryId: string, sortOrder: number): Promise<OpportunitySubcategory> => {
  const response = await api.post("/api/v1/opportunity-subcategories", { name, category_id: categoryId, sort_order: sortOrder });
  return response.data;
};

export const apiUpdateSubcategory = async (id: string, name: string, sortOrder: number): Promise<void> => {
  await api.patch(`/api/v1/opportunity-subcategories/${id}`, { name, sort_order: sortOrder });
};

export const apiDeleteSubcategory = async (id: string, reassignTo?: string): Promise<void> => {
  const params: Record<string, string> = {};
  if (reassignTo) params.reassignTo = reassignTo;
  await api.delete(`/api/v1/opportunity-subcategories/${id}`, { params });
};
